"use client";
import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import Image from "next/image";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "react-hot-toast";
import LoginRegistrationModal from "./LoginRegistrationModal";

const defaultImages = [
  "/memory_demo/1.png",
  "/memory_demo/2.png",
  "/memory_demo/3.png",
  "/memory_demo/4.png",
  "/memory_demo/5.png",
];

const ShippingNotifications = ({
  set_Id,
  setModal,
  images = defaultImages,
  blurredImages = false,
  // what the shared message links to: the obituary the cards belong to
  slugKey = "",
  deceasedName = "",
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const widthDivRef = useRef(null);
  const heightDivRef = useRef(null);
  const [width, setWidth] = useState(0);
  const [height, setHeight] = useState(0);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const { user, isAuthenticated } = useAuth();

  const finalImages = useMemo(() => {
    const filledImages = images.map((img, idx) => {
      if (img && typeof img === "string" && img.trim() !== "") {
        return img;
      }
      return defaultImages[idx] || defaultImages[0];
    });
    return filledImages;
  }, [images]);

  useEffect(() => {
    const widthDiv = widthDivRef.current;
    const heightDiv = heightDivRef.current;
    if (!widthDiv || !heightDiv) return;

    const handleResize = () => {
      if (widthDiv) {
        setWidth(widthDiv.offsetWidth);
      }
      if (heightDiv) {
        setHeight(heightDiv.offsetHeight);
      }
    };

    const resizeObserver = new ResizeObserver(handleResize);
    resizeObserver.observe(widthDiv);
    resizeObserver.observe(heightDiv);

    // Initial width and height set
    handleResize();

    return () => {
      resizeObserver.unobserve(widthDiv);
      resizeObserver.unobserve(heightDiv);
      resizeObserver.disconnect();
    };
  }, []);

  const handlePrev = () => {
    setCurrentIndex((prevIndex) =>
      prevIndex === 0 ? prevIndex : prevIndex - 1
    );
  };

  const handleNext = () => {
    setCurrentIndex((prevIndex) =>
      prevIndex === images.length - 1 ? prevIndex : prevIndex + 1
    );
  };

  // Actual download (no auth check). Used by handleDownload and by post-login retry so we don't depend on session propagation.
  const performDownload = useCallback(async () => {
    try {
      const currentImageUrl = finalImages[currentIndex];
      const absoluteUrl =
        currentImageUrl.startsWith("http://") || currentImageUrl.startsWith("https://")
          ? currentImageUrl
          : new URL(currentImageUrl, window.location.origin).href;

      const response = await fetch(absoluteUrl);
      if (!response.ok) throw new Error("Failed to fetch image");

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);

      let extension = "png";
      const urlMatch = currentImageUrl.match(/\.([a-zA-Z0-9]+)(?:\?|$)/);
      if (urlMatch && urlMatch[1]) {
        extension = urlMatch[1].toLowerCase();
      } else if (blob.type) {
        const mimeMatch = blob.type.match(/image\/([a-zA-Z0-9]+)/);
        if (mimeMatch && mimeMatch[1]) {
          const mimeType = mimeMatch[1].toLowerCase();
          extension = mimeType === "jpeg" ? "jpg" : mimeType;
        }
      }

      const a = document.createElement("a");
      a.href = url;
      a.download = `digitalna_kartica_${currentIndex + 1}.${extension}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);

      toast.success("Kartica je bila uspešno prenesena");
    } catch (error) {
      console.error("Download error:", error);
      toast.error("Prenos kartice ni uspel");
    }
  }, [finalImages, currentIndex]);

  // Phones: the system share sheet (WhatsApp, Viber, SMS, ...) with the card picture and the link to the obituary.
  // Nothing is stored and no login is needed; the recipient is chosen in the app the visitor picks.
  const handleShare = useCallback(async () => {
    const currentImageUrl = finalImages[currentIndex];
    const link = slugKey ? `${window.location.origin}/m/${slugKey}` : window.location.href;
    const text = `${deceasedName ? `${deceasedName}\n` : ""}${link}`;
    const title = deceasedName || "Osmrtnica";
    try {
      const absoluteUrl = /^https?:\/\//.test(currentImageUrl)
        ? currentImageUrl
        : new URL(currentImageUrl, window.location.origin).href;
      let file = null;
      try {
        const response = await fetch(absoluteUrl);
        if (response.ok) {
          const blob = await response.blob();
          const type = blob.type || "image/png";
          const extension = type.split("/")[1]?.replace("jpeg", "jpg") || "png";
          file = new File([blob], `digitalna_kartica_${currentIndex + 1}.${extension}`, { type });
        }
      } catch {
        file = null;
      }
      if (file && navigator.canShare?.({ files: [file] })) {
        await navigator.share({ files: [file], title, text });
      } else if (navigator.share) {
        // the picture cannot be shared as a file here: the link to the obituary still can
        await navigator.share({ title, text, url: link });
      } else {
        await performDownload();
      }
    } catch (error) {
      // the visitor closing the share sheet is not an error
      if (error?.name !== "AbortError") {
        console.error("Share error:", error);
        toast.error("Pošiljanje kartice ni uspelo");
      }
    }
  }, [finalImages, currentIndex, slugKey, deceasedName, performDownload]);

  const handleDownload = useCallback(async () => {
    if (!isAuthenticated || !user) {
      toast.error("Za prenos kartice je potrebna prijava");
      setIsLoginModalOpen(true);
      return;
    }
    await performDownload();
  }, [isAuthenticated, user, performDownload]);

  const ImageSliderBlock = ({ image, index }) => {
    return (
      <>
        {index === currentIndex ? (
          <div className="h-[500px]  w-[260px] " key={index}>
            <div className="mockup-phone h-[500px] w-[260px] shadow-custom-light-dark-box-image-wall ">
              <div style={{ height: 25, width: 120 }} className=" camera" />
              <div className="display w-full h-full flex justify-center items-center">
                <img
                  key={index}
                  src={image}
                  alt={`Slide ${index}`}
                  className={`relative object-cover h-full w-full`}
                  onError={(e) => {
                    e.target.src = defaultImages[index];
                  }}
                />
              </div>
            </div>
          </div>
        ) : index > currentIndex ? (
          <img
            key={index}
            src={image}
            alt={`Slide ${index}`}
            className={`elative hidden desktop:inline object-cover my-auto h-[300px] w-[156px] ml-4 rounded shadow-custom-light-dark-box-image-wall  `}
            onError={(e) => {
              e.target.src = defaultImages[index];
            }}
          />
        ) : null}
      </>
    );
  };

  return (
    <div className="relative bg-[url('../public/pisano_ozadje.avif')]  bg-cover border-t-[1px] border-[#0F0671]  py-24 tablet:py[65px] w-full overflow-hidden mx-auto justify-center items-center flex flex-col">
      <div className="max-w-[1000px] memory_cards tablet:w-[700px] w-full flex flex-row mobile:flex-col gap-[77px] tablet:gap-[50px] mobile:gap-[39px] justify-evenly mobile:px-7">
        <div className="w-[368.5px] tablet:w-[347px] shrink-0 mobile:pl-0 mobile:w-full mx-3 mobile:mx-0 mobile:text-center  ">
          <h2 className="text-[40px] leading-[100%] mobile:text-[28px] mobile:leading-[33px] whitespace-nowrap font-normal text-[#1E2125]">
            Digitalne žalne kartice
          </h2>
          <div className="text-[16px] leading-6 mt-4 font-normal text-[#414141]">
            Prenesite si že pripravljene digitalne kartice za pošiljanje naprej preko mobilnega telefona.
            <br /> <br />
            S pošiljanjem drugim lahko začnete takoj.
          </div>
          <div className="text-[20px] font-normal text-[#0977AE] mt-[65px] mobile:mt-[30px]">
            Obvestilo o pogrebu
          </div>
          <div className="text-[20px] font-normal text-[#0977AE] mt-2">
            Zahvala
          </div>
          <div className="text-[20px] font-normal text-[#0977AE] mt-2">
            Vabilo
          </div>
          <div className="mobile:hidden mt-8">
            <div className="text-[20px] font-normal text-[#1E2125]">
              Digitalne kartice so BREZPLAČNE.
            </div>
          </div>
          <div className="mobile:hidden flex flex-col items-start mt-24 mobile:justify-center whitespace-nowrap">
            <div className="text-[16px] font-normal text-[#414141] whitespace-nowrap">
              Op. Kartico je mogoče prenesti z mobilnega telefona.
            </div>
          </div>
        </div>
        <div className="relative flex w-full desktop:ml-10 flex-col">
          <div
            ref={widthDivRef}
            style={{
              height: height,
            }}
            className="relative flex w-full flex-col"
          />
          <div
            style={{
              width: width,
            }}
            ref={heightDivRef}
            className="flex absolute desktop:right-0 tablet:right-0 flex-col"
          >
            <div className="relative  flex-1  flex flex-row mobile:justify-center">
              {finalImages.map((image, index) => (
                <ImageSliderBlock image={image} index={index} key={index} />
              ))}
            </div>

            <div className="flex justify-center items-center gap-[58px] tablet:gap-[20px] mobile:gap-[10px] mobile:my-[20px] desktop:ml-[350px] desktop:translate-y-[-100%] tablet:mt-[27px]">
              <button
                className="flex justify-center items-center h-[74px] w-[74px] "
                onClick={handlePrev}
              >
                <Image
                  src="/memory_demo_left.png"
                  alt="arrow-left"
                  width={74}
                  height={74}
                />
              </button>
              <div className="inline-flex flex-row gap-[10px] desktop:hidden">
                {images.map((image, index) => (
                  <div key={index}>
                    {index === currentIndex ? (
                      <div
                        key={index}
                        className="w-[16px] h-[16px] bg-white rounded-full"
                        style={{
                          background:
                            "linear-gradient(113.63deg, #E3E8EC 0%, #FFFFFF 100%)",
                          boxShadow:
                            "5px 5px 10px 0px #C2C2C280, -5px -5px 10px 0px #FFFFFF",
                        }}
                      ></div>
                    ) : (
                      <div
                        key={index}
                        className="w-[16px] h-[16px] bg-white rounded-full"
                        style={{
                          background:
                            "linear-gradient(113.63deg, #C3C6C8 0%, #E3E5E5 100%)",
                        }}
                      ></div>
                    )}
                  </div>
                ))}
              </div>
              <button
                className="flex justify-center items-center h-[74px] w-[74px]"
                onClick={handleNext}
              >
                <Image
                  src="/memory_demo_right.png"
                  alt="arrow-right"
                  width={74}
                  height={74}
                />
              </button>
            </div>

            {/* Download section - Mobile only */}
            <div className="hidden mobile:flex flex-col items-center mt-5">
              <p className="text-[16px] leading-[20px] font-normal text-[#414141] text-center mb-9 px-4">
                Izberite kartico, nato aplikacijo (WhatsApp, Viber, SMS ...) in jo pošljite naprej
              </p>
              <div className="w-[266px] shadow-custom-dark-bottom rounded-[10px]">
                <div className="h-[60px] w-[100%] bg-gradient-to-b from-[#0A85C2] to-[#123597] flex justify-center items-center rounded-[10px] p-[2px]">
                  <div
                    onClick={handleShare}
                    className="h-[56px] w-[100%] bg-[#E7EBF0] text-[20px] text-[#414B5A] flex justify-center items-center rounded-[8px] font-variation-customOpt20 font-semibold leading-[24px] cursor-pointer"
                  >
                    Pošlji
                  </div>
                </div>
              </div>
            </div>

            <div className="mobile:flex hidden flex-col items-center mt-5 whitespace-nowrap">
              {/* Row for BREZPLAČNO + v vaši cvetličarni */}
              <div className="flex flex-row items-end mobile:justify-center">
                <div
                  onClick={() => {
                    set_Id("drugega");
                    setModal(true);
                  }}
                  className="text-[17px] cursor-pointer leading-[28px] font-normal text-[#1E2125]"
                >
                  Digitalne kartice so BREZPLAČNE.
                </div>
                {/* <div className="text-[20px] font-normal text-[#1E2125] ml-[5px]">
                  v vaši cvetličarni
                </div> */}
              </div>

              {/* Paragraph underneath */}
              {/* <p className="text-[#6D778E] text-[14px] jdmobile:text-[12px] pt-3 text-center">
                Op. Datum in kraj pogreba ob času objave osmrtnice običajno
                <br />
                niso znani in so vnešeni kasneje (velja za prve tri kartice).
              </p> */}
            </div>
          </div>
        </div>
      </div>

      {/* Login Modal */}
      <LoginRegistrationModal
        isOpen={isLoginModalOpen}
        onClose={() => {
          setIsLoginModalOpen(false);
        }}
        onLoginSuccess={() => {
          setIsLoginModalOpen(false);
          // Run download directly (skip auth check) so popup doesn't reopen before session propagates
          setTimeout(() => performDownload(), 300);
        }}
        initialTab="login"
      />
    </div>
  );
};

export default ShippingNotifications;
